import { Logger } from "@cede/utils";
import { z } from "zod";

// Schema for client-side NEXT_PUBLIC_ environment variables used in hub-v2
const ClientEnvSchema = z.object({
  // Authentication & Integrations
  NEXT_PUBLIC_ETHERMAIL_WIDGET_ID: z.string().optional(), // Optional based on usage in useEthermail
  NEXT_PUBLIC_PRIVY_APP_ID: z.string(),
  NEXT_PUBLIC_MIXPANEL_TOKEN: z.string().optional(), // From mixpanel/index.ts
  NEXT_PUBLIC_MIXPANEL_PROXY_DOMAIN: z.string().optional(), // From mixpanel/index.ts
  NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY: z.string().min(1, { error: "NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY is required" }),
  NEXT_PUBLIC_TURNSTILE_PUBLIC_KEY: z.string().min(1, { error: "NEXT_PUBLIC_TURNSTILE_PUBLIC_KEY is required" }),
  NEXT_PUBLIC_TG_BOT_LINK: z.url(),
  NEXT_PUBLIC_TG_ANALYTICS_TOKEN: z.string(),
  NEXT_PUBLIC_TG_ANALYTICS_IDENTIFIER: z.string(),
  // API & Environment
  NEXT_PUBLIC_CEDE_API_URL: z.url(),
  NEXT_PUBLIC_KENOBI_API_URL: z.url(),
  NEXT_PUBLIC_CEDE_API_TOKEN: z.string().min(1, { error: "NEXT_PUBLIC_CEDE_API_TOKEN is required" }),
  NEXT_PUBLIC_CEDE_NODE_ENV: z.enum(["development", "production", "staging", "local"]).default("development"),
  /**
   * Forces the consent banner on where it is normally suppressed. Set it on a
   * preview deployment to exercise the banner; production never reads it.
   */
  NEXT_PUBLIC_CONSENT_BANNER_FORCE: z.stringbool().default(false),
  NEXT_PUBLIC_REFRESH_CLEARANCE_TURNSTILE_SITE_KEY: z.string().min(1, {
    error: "NEXT_PUBLIC_REFRESH_CLEARANCE_TURNSTILE_SITE_KEY is required",
  }),
  NEXT_PUBLIC_OAUTH_TELEGRAM_BOT_ID: z.string().min(1, { error: "NEXT_PUBLIC_TELEGRAM_BOT_ID is required" }),
  NEXT_PUBLIC_APP_URL: z.url(),
  NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
  // Sentry environment label, falling back to NODE_ENV when NEXT_PUBLIC_SENTRY_ENV is unset.
  // Used both as the Sentry `environment` tag and as the gate that limits init to production deploys.
  // Kept as a free-form string so an unexpected label (e.g. "preview") never crashes the bundle at load time;
  // the gate only checks for the literal "production".
  NEXT_PUBLIC_SENTRY_ENV: z.string().default("development"),
  NEXT_PUBLIC_SENTRY_RELEASE: z.string().default(""),
  NEXT_PUBLIC_TMA_DEV: z.string().transform((val) => val === "true"),
  // Local-rewards-e2e harness overrides (development/local-rewards-e2e). Unset in
  // every deployed environment — when set, hub claims target the rewards module
  // deployed on the local anvil forks and route mapped chains' RPC traffic there.
  NEXT_PUBLIC_LOCAL_REWARDS_SAFE_MODULE_ADDRESS: z
    .string()
    .regex(/^0x[0-9a-fA-F]{40}$/, { error: "NEXT_PUBLIC_LOCAL_REWARDS_SAFE_MODULE_ADDRESS must be a 0x address" })
    .optional(),
  NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS: z.string().optional(),
});

/**
 * Parse NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS — a JSON map of chainId → RPC URL,
 * e.g. {"1":"http://127.0.0.1:8545","42161":"http://127.0.0.1:8546"}, used only
 * by the local-rewards-e2e harness. Returns undefined when unset. A malformed
 * value throws (no silent fallback): a broken local override must surface
 * loudly instead of quietly sending claim txs to the default public RPCs.
 */
const parseLocalRewardsRpcUrls = (raw: string | undefined): Record<string, string> | undefined => {
  if (!raw) return undefined;
  const parsed: unknown = JSON.parse(raw);
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS must be a JSON object of chainId → RPC URL");
  }
  for (const [chainId, url] of Object.entries(parsed)) {
    if (!/^\d+$/.test(chainId) || typeof url !== "string" || url.length === 0) {
      throw new Error(
        `NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS entry "${chainId}" is invalid — expected integer chainId → non-empty URL`,
      );
    }
  }
  return parsed as Record<string, string>;
};

// Validate process.env at build time (Next.js ensures these are available)
// If validation fails here, the build process should ideally catch it.
let validatedEnv: z.infer<typeof ClientEnvSchema>;
try {
  validatedEnv = ClientEnvSchema.parse({
    NEXT_PUBLIC_ETHERMAIL_WIDGET_ID: process.env.NEXT_PUBLIC_ETHERMAIL_WIDGET_ID,
    NEXT_PUBLIC_PRIVY_APP_ID: process.env.NEXT_PUBLIC_PRIVY_APP_ID,
    NEXT_PUBLIC_MIXPANEL_TOKEN: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
    NEXT_PUBLIC_MIXPANEL_PROXY_DOMAIN: process.env.NEXT_PUBLIC_MIXPANEL_PROXY_DOMAIN,
    NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY,
    NEXT_PUBLIC_TURNSTILE_PUBLIC_KEY: process.env.NEXT_PUBLIC_TURNSTILE_PUBLIC_KEY,
    NEXT_PUBLIC_TG_BOT_LINK: process.env.NEXT_PUBLIC_TG_BOT_LINK,
    NEXT_PUBLIC_CEDE_API_URL: process.env.NEXT_PUBLIC_CEDE_API_URL,
    NEXT_PUBLIC_CEDE_API_TOKEN: process.env.NEXT_PUBLIC_CEDE_API_TOKEN,
    NEXT_PUBLIC_KENOBI_API_URL: process.env.NEXT_PUBLIC_KENOBI_API_URL,
    NEXT_PUBLIC_CEDE_NODE_ENV: process.env.NEXT_PUBLIC_CEDE_NODE_ENV,
    NEXT_PUBLIC_CONSENT_BANNER_FORCE: process.env.NEXT_PUBLIC_CONSENT_BANNER_FORCE,
    NEXT_PUBLIC_REFRESH_CLEARANCE_TURNSTILE_SITE_KEY: process.env.NEXT_PUBLIC_REFRESH_CLEARANCE_TURNSTILE_SITE_KEY,
    NEXT_PUBLIC_OAUTH_TELEGRAM_BOT_ID: process.env.NEXT_PUBLIC_OAUTH_TELEGRAM_BOT_ID,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_TG_ANALYTICS_TOKEN: process.env.NEXT_PUBLIC_TG_ANALYTICS_TOKEN,
    NEXT_PUBLIC_TG_ANALYTICS_IDENTIFIER: process.env.NEXT_PUBLIC_TG_ANALYTICS_IDENTIFIER,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    // Fallback chain: explicit Sentry env > hub-v2's CEDE_NODE_ENV (set per deploy in CI) > NODE_ENV.
    // NODE_ENV is "production" in every deployed Next.js build (incl. preview/staging), so on its own
    // it's a poor gate. NEXT_PUBLIC_CEDE_NODE_ENV is the real deploy-environment label.
    NEXT_PUBLIC_SENTRY_ENV:
      process.env.NEXT_PUBLIC_SENTRY_ENV ?? process.env.NEXT_PUBLIC_CEDE_NODE_ENV ?? process.env.NODE_ENV,
    NEXT_PUBLIC_SENTRY_RELEASE: process.env.NEXT_PUBLIC_SENTRY_RELEASE ?? process.env.SENTRY_RELEASE,
    NEXT_PUBLIC_TMA_DEV: process.env.NEXT_PUBLIC_TMA_DEV,
    NEXT_PUBLIC_LOCAL_REWARDS_SAFE_MODULE_ADDRESS: process.env.NEXT_PUBLIC_LOCAL_REWARDS_SAFE_MODULE_ADDRESS,
    NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS: process.env.NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS,
  });
} catch (error) {
  Logger.error("❌ Invalid client environment variables:", error instanceof z.ZodError ? z.treeifyError(error) : error);
  // In a real build process, this might throw or exit to fail the build
  // For runtime, we might use default values or log extensively.
  // Since Next.js handles build-time checks for NEXT_PUBLIC_, we assume valid structure here
  // but log errors if parsing somehow fails at runtime (e.g., unexpected types).
  validatedEnv = ClientEnvSchema.parse({}); // Attempt to parse with defaults/handle error state
}

// Export a frozen, typed config object for client-side use
export const clientConfig = Object.freeze({
  ethermailWidgetId: validatedEnv.NEXT_PUBLIC_ETHERMAIL_WIDGET_ID,
  privyAppId: validatedEnv.NEXT_PUBLIC_PRIVY_APP_ID,
  mixpanelToken: validatedEnv.NEXT_PUBLIC_MIXPANEL_TOKEN,
  mixpanelProxyDomain: validatedEnv.NEXT_PUBLIC_MIXPANEL_PROXY_DOMAIN,
  recaptchaPublicKey: validatedEnv.NEXT_PUBLIC_RECAPTCHA_PUBLIC_KEY,
  turnstilePublicKey: validatedEnv.NEXT_PUBLIC_TURNSTILE_PUBLIC_KEY,
  telegramBotLink: validatedEnv.NEXT_PUBLIC_TG_BOT_LINK,
  apiUrl: validatedEnv.NEXT_PUBLIC_CEDE_API_URL,
  apiToken: validatedEnv.NEXT_PUBLIC_CEDE_API_TOKEN,
  nodeEnv: validatedEnv.NEXT_PUBLIC_CEDE_NODE_ENV,
  consentBannerForce: validatedEnv.NEXT_PUBLIC_CONSENT_BANNER_FORCE,
  kenobiApiUrl: validatedEnv.NEXT_PUBLIC_KENOBI_API_URL,
  refreshClearanceTurnstileSiteKey: validatedEnv.NEXT_PUBLIC_REFRESH_CLEARANCE_TURNSTILE_SITE_KEY,
  telegramBotId: validatedEnv.NEXT_PUBLIC_OAUTH_TELEGRAM_BOT_ID,
  appUrl: validatedEnv.NEXT_PUBLIC_APP_URL,
  telegramAnalyticsToken: validatedEnv.NEXT_PUBLIC_TG_ANALYTICS_TOKEN,
  telegramAnalyticsIdentifier: validatedEnv.NEXT_PUBLIC_TG_ANALYTICS_IDENTIFIER,
  sentryDsn: validatedEnv.NEXT_PUBLIC_SENTRY_DSN,
  sentryEnv: validatedEnv.NEXT_PUBLIC_SENTRY_ENV,
  sentryRelease: validatedEnv.NEXT_PUBLIC_SENTRY_RELEASE,
  tmaDev: validatedEnv.NEXT_PUBLIC_TMA_DEV,
  // Local-rewards-e2e harness overrides — both undefined in all deployed environments.
  localRewardsSafeModuleAddress: validatedEnv.NEXT_PUBLIC_LOCAL_REWARDS_SAFE_MODULE_ADDRESS,
  localRewardsRpcUrls: parseLocalRewardsRpcUrls(validatedEnv.NEXT_PUBLIC_LOCAL_REWARDS_RPC_URLS),
});
